<template>
  <div>
    <b-row
      class="content-header"
    >

      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title float-left pr-1 mb-0">
              Entitlements Obligations Report
            </h1>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <!-- Table Container Card -->
    <validation-observer
      ref="entitlementsObligationReportForm"
      #default="{invalid}"
    >
      <b-form @submit.prevent="generateReport">
        <b-card
          header-tag="header"
        >
          <b-row>
            <b-col
              cols="md-12"
            >
              <validation-provider
                #default="{ errors }"
                name="Filter by Inventory Item"
                vid="inventory"
                rules="required"
              >
                <b-form-group
                  label="Filter by Inventory Item"
                  label-for="inventory-name"
                  label-cols-md="1"
                  :state="(errors.length > 0 || inventoryValidation) ? false : null"
                >
                  <b-form-tags
                    v-model="inventoryItems"
                    size="lg"
                    add-on-change
                    no-outer-focus
                  >
                    <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                      <ul
                        v-if="tags.length > 0"
                        class="list-inline d-inline-block mb-1 user__lists"
                      >
                        <li
                          v-for="tag in tags"
                          :key="tag"
                          class="list-inline-item"
                        >
                          <b-form-tag
                            :title="tag"
                            :disabled="disabled"
                            variant="danger"
                            @remove="removeTag(tag)"
                          >
                            {{ resolveItemName(tag) }}
                          </b-form-tag>
                        </li>
                      </ul>
                      <b-form-select
                        v-bind="inputAttrs"
                        v-on="inputHandlers"
                        @input="updateOptionData"
                      >
                        <template v-slot:first>
                          <!-- This is required to prevent bugs with Safari -->
                          <option
                            disabled
                            value=""
                          >
                            Select inventory items...
                          </option>
                        </template>
                        <option
                          v-for="(opt, ind) in inventoryOptions"
                          :key="ind"
                          :value="opt._id"
                        >
                          {{ opt.name }}
                        </option>
                      </b-form-select>
                    </template>
                  </b-form-tags>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="inventoryValidation"
                    class="text-danger"
                  >
                    {{ inventoryError }}
                  </small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="md-12"
            >
              <validation-provider
                #default="{ errors }"
                name="Filter by Entitlement Scheme"
                vid="scheme"
              >
                <b-form-group
                  label="Filter by Entitlement Scheme"
                  label-for="scheme-name"
                  label-cols-md="1"
                  :state="(errors.length > 0 || schemeValidation) ? false : null"
                >
                  <b-form-tags
                    v-model="schemes"
                    size="lg"
                    add-on-change
                    no-outer-focus
                  >
                    <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                      <ul
                        v-if="tags.length > 0"
                        class="list-inline d-inline-block mb-1 user__lists"
                      >
                        <li
                          v-for="tag in tags"
                          :key="tag"
                          class="list-inline-item"
                        >
                          <b-form-tag
                            :title="tag"
                            :disabled="disabled"
                            variant="danger"
                            @remove="removeTag(tag)"
                          >
                            {{ resolveSchemeName(tag) }}
                          </b-form-tag>
                        </li>
                      </ul>
                      <b-form-select
                        v-bind="inputAttrs"
                        v-on="inputHandlers"
                        @input="updateSchemeData"
                      >
                        <template v-slot:first>
                          <!-- This is required to prevent bugs with Safari -->
                          <option
                            disabled
                            value=""
                          >
                            Select entitlement scheme...
                          </option>
                        </template>
                        <option
                          v-for="(opt, ind) in schemeOptions"
                          :key="ind"
                          :value="opt._id"
                        >
                          {{ opt.name }}
                        </option>
                      </b-form-select>
                    </template>
                  </b-form-tags>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="schemeValidation"
                    class="text-danger"
                  >
                    {{ schemeError }}
                  </small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="md-12"
            >
              <validation-provider
                #default="{ errors }"
                name="Issuance Date"
                vid="date"
                rules="required"
                class="date__picker_block white-bg-dp mr-2"
              >
                <b-form-group
                  label="Issuance Date Range"
                  label-cols-md="1"
                  :state="(errors.length > 0 || dateValidation) ? false : null"
                >
                  <b-input-group>
                    <flat-pickr
                      id="main-date"
                      v-model="dateRange"
                      class="form-control"
                      placeholder="Select a date range"
                      :config="flatPickrConfig"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        icon="CalendarIcon"
                        class="cursor-pointer"
                        data-toggle
                        size="18"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="dateValidation"
                    class="text-danger"
                  >
                    {{ dateError }}
                  </small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="md-12"
            >
              <validation-provider
                #default="{ errors }"
                name="Filter by Status"
                vid="status"
              >
                <b-form-group
                  label="Filter by Status"
                  label-for="status-name"
                  label-cols-md="1"
                  :state="(errors.length > 0 || statusValidation) ? false : null"
                >
                  <b-form-tags
                    v-model="statuses"
                    size="lg"
                    add-on-change
                    no-outer-focus
                  >
                    <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                      <ul
                        v-if="tags.length > 0"
                        class="list-inline d-inline-block mb-1 user__lists"
                      >
                        <li
                          v-for="tag in tags"
                          :key="tag"
                          class="list-inline-item"
                        >
                          <b-form-tag
                            :title="tag"
                            :disabled="disabled"
                            variant="danger"
                            @remove="removeTag(tag)"
                          >
                            {{ resolveStatusName(tag) }}
                          </b-form-tag>
                        </li>
                      </ul>
                      <b-form-select
                        v-bind="inputAttrs"
                        v-on="inputHandlers"
                        @input="updateStatusData"
                      >
                        <template v-slot:first>
                          <!-- This is required to prevent bugs with Safari -->
                          <option
                            disabled
                            value=""
                          >
                            Select status...
                          </option>
                        </template>
                        <option
                          v-for="(opt, ind) in statusOptions"
                          :key="ind"
                          :value="opt._id"
                        >
                          {{ opt.name }}
                        </option>
                      </b-form-select>
                    </template>
                  </b-form-tags>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="statusValidation"
                    class="text-danger"
                  >
                    {{ statusError }}
                  </small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="md-12"
            >
              <validation-provider
                #default="{ errors }"
                name="Filter by Store"
                vid="store"
              >
                <b-form-group
                  label="Filter by Store"
                  label-for="store-name"
                  label-cols-md="1"
                  :state="(errors.length > 0 || storeValidation) ? false : null"
                >
                  <b-form-tags
                    v-model="stores"
                    size="lg"
                    add-on-change
                    no-outer-focus
                  >
                    <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                      <ul
                        v-if="tags.length > 0"
                        class="list-inline d-inline-block mb-1 user__lists"
                      >
                        <li
                          v-for="tag in tags"
                          :key="tag"
                          class="list-inline-item"
                        >
                          <b-form-tag
                            :title="tag"
                            :disabled="disabled"
                            variant="danger"
                            @remove="removeTag(tag)"
                          >
                            {{ resolveStoreName(tag) }}
                          </b-form-tag>
                        </li>
                      </ul>
                      <b-form-select
                        v-bind="inputAttrs"
                        v-on="inputHandlers"
                        @input="updateStoreData"
                      >
                        <template v-slot:first>
                          <!-- This is required to prevent bugs with Safari -->
                          <option
                            disabled
                            value=""
                          >
                            Select store...
                          </option>
                        </template>
                        <option
                          v-for="(opt, ind) in storeOptions"
                          :key="ind"
                          :value="opt._id"
                        >
                          {{ opt.name }}
                        </option>
                      </b-form-select>
                    </template>
                  </b-form-tags>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="storeValidation"
                    class="text-danger"
                  >
                    {{ storeError }}
                  </small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="md-6" />
            <b-col cols="md-6">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="button"
                variant="primary"
                class="float-right"
                :disabled="invalid"
                @click="generateReport"
              >
                <span class="align-middle">Generate Report</span>
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-form>
    </validation-observer>

    <div v-if="showUrnReportCard">
      <b-card
        v-for="(urnData, key) in urnReportData"
        :key="key"
        header-tag="header"
        class="chart-card report__card"
      >
        <template
          v-if="key == 0"
          #header
        >
          <h3 class="align-middle mr-auto">
            Entitlement Obligation Report  ({{ dateRangeValue }})
          </h3>
          <b-button
            type="button"
            variant="flat-primary"
            class="float-right"
            @click="downloadExcel()"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
              size="20"
            />
            <span class="">Download Excel</span>
          </b-button>
        </template>
        <b-row>
          <b-col cols="12">
            <div class="custom-thead-color p-1 d-flex justify-content-between align-items-center">

              <!-- Left -->
              <div class="d-flex align-items-center">
                <b-img
                  class="img-preview-block"
                  :src="getItemImage(urnData.id)"
                />
                <span class="ml-1 custom-thead-item-title">
                  {{ resolveItemName(urnData.id) }}
                </span>
              </div>

              <!-- Right -->
              <div class="d-flex align-items-center">
                <span class="mr-2 custom-thead-item-title">
                  Total Qty: {{ urnData.totalQty || 0 }}
                </span>

                <span class="mr-2 custom-thead-item-title">
                  Current In-Store Qty: {{ urnData.totalInStockQty || 0 }}
                </span>

                <feather-icon
                  :icon="expandedItems[key] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                  class="cursor-pointer"
                  @click="toggleItem(key)"
                />
              </div>

            </div>
          </b-col>
        </b-row>

        <!-- ONLY table is collapsible -->
        <b-row v-if="expandedItems[key]">
          <b-col cols="12">
            <b-table
              :id="'urnTable' + key"
              class="position-relative"
              :items="urnData.transactions"
              responsive
              :fields="tableColumns"
              show-empty
              no-sort-reset
              :sort-by="sortBy"
              :sort-desc="sortDirDesc"
              sort-compare-locale="zh"
              empty-text="No matching records found"
            >
              <template #cell(entitlementID)="data">
                <div class="text-nowrap">
                  <span class="text-bold-black">E{{ data.item.entitlementID.padStart(6, '0') }}</span>
                </div>
              </template>
              <template #cell(customerID)="data">
                <div class="text-nowrap">
                  <span class="text-bold-black">{{ data.item.customerID }}</span>
                </div>
              </template>
              <template #cell(fileID)="data">
                <div class="text-nowrap">
                  <span>{{ data.item.fileID }}</span>
                </div>
              </template>
              <template #cell(createdAt)="data">
                <div class="text-nowrap">
                  <span>{{ data.item.createdAt ? dateFormat(data.item.createdAt) : '-' }}</span>
                </div>
              </template>
              <template #cell(quantity)="data">
                <div class="text-nowrap">
                  <span>{{ data.item.quantity }}</span>
                </div>
              </template>
              <template #cell(status)="data">
                <div class="text-nowrap">
                  <b-badge
                    :variant="`light-${resolveEntitlementStatusVariant(data.item.status)}`"
                    class="text-capitalize"
                  >
                    {{ data.item.status }}
                  </b-badge>
                </div>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </b-card>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BForm, BButton, BFormTags, BFormTag, BFormSelect, BFormGroup,
  BInputGroup, BInputGroupAppend, BTable, BImg, BBadge,
} from 'bootstrap-vue'
// eslint-disable-next-line import/no-unresolved
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import { writeFile, utils } from 'xlsx'
import flatPickr from 'vue-flatpickr-component'
import '@core/scss/vue/libs/vue-flatpicker.scss'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BButton,
    BFormTags,
    BFormTag,
    BFormSelect,
    BFormGroup,
    BInputGroup,
    BTable,
    BInputGroupAppend,
    BImg,
    BBadge,

    flatPickr,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      // eslint-disable-next-line global-require
      defaultImage: require('@/assets/images/placeholder/placeholder_image.png'),
      showUrnReportCard: false,
      stores: [],
      storeError: 'Valid urn is required',
      storeValidation: false,
      inventoryItems: [],
      inventoryError: 'Valid urn is required',
      inventoryValidation: false,
      schemes: [],
      schemeError: 'Valid urn is required',
      schemeValidation: false,
      statuses: [],
      statusError: 'Valid urn is required',
      statusValidation: false,
      dateError: 'Valid date is required',
      dateRangeData: '',
      dateRange: '',
      dateValidation: false,
      flatPickrConfig: {
        wrap: true, mode: 'range', dateFormat: 'd/m/Y', enableTime: false, disableMobile: true,
      },
      submittedDate: '',
      inventoryOptions: [
        {
          name: 'Select All',
          _id: 'all',
        },
      ],
      storeOptions: [
        {
          name: 'Select All',
          _id: 'all',
        },
        {
          name: 'Main Store',
          _id: 'Main Store',
        },
        {
          name: 'Block A',
          _id: 'Block A',
        },
        {
          name: 'Block B',
          _id: 'Block B',
        },
        {
          name: 'Block C',
          _id: 'Block C',
        },
        {
          name: 'Block D',
          _id: 'Block D',
        },
        {
          name: 'Block E',
          _id: 'Block E',
        },
      ],
      schemeOptions: [
        {
          name: 'Select All',
          _id: 'all',
        },
      ],
      statusOptions: [
        {
          name: 'Select All',
          _id: 'all',
        },
        {
          name: 'Redeemable',
          _id: 'redeemable',
        },
        {
          name: 'Redeemed',
          _id: 'redeemed',
        },
        {
          name: 'Used',
          _id: 'used',
        },
        {
          name: 'Expired',
          _id: 'expired',
        },
        {
          name: 'Cancelled',
          _id: 'cancelled',
        },
      ],
      onlyInventoryOptions: [],
      onlyStoreOptions: [
        {
          name: 'Main Store',
          _id: 'Main Store',
        },
        {
          name: 'Block A',
          _id: 'Block A',
        },
        {
          name: 'Block B',
          _id: 'Block B',
        },
        {
          name: 'Block C',
          _id: 'Block C',
        },
        {
          name: 'Block D',
          _id: 'Block D',
        },
        {
          name: 'Block E',
          _id: 'Block E',
        },
      ],
      onlySchemeOptions: [],
      onlyStatusOptions: [
        {
          name: 'Redeemable',
          _id: 'redeemable',
        },
        {
          name: 'Redeemed',
          _id: 'redeemed',
        },
        {
          name: 'Used',
          _id: 'used',
        },
        {
          name: 'Expired',
          _id: 'expired',
        },
        {
          name: 'Cancelled',
          _id: 'cancelled',
        },
      ],
      urnReportData: [],
      tableColumns: [
        {
          key: 'entitlementID',
          label: 'ent. id',
          sortable: false,
        },
        {
          key: 'fileID',
          label: 'File Number',
          sortable: false,
        },
        {
          key: 'createdAt',
          label: 'Issuance Date',
          sortable: false,
        },
        {
          key: 'customerID',
          label: 'Customer',
          sortable: false,
        },
        {
          key: 'store',
          label: 'Store',
          sortable: false,
        },
        {
          key: 'quantity',
          label: 'Quantity',
          sortable: false,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: false,
        },
        {
          key: 'internalRemarks',
          label: 'Internal Remarks',
          sortable: false,
        },
      ],
      urnTableData: [],
      sortBy: 'id',
      sortDirDesc: false,
      expandedItems: {},
      // validation rules
      required,
    }
  },

  computed: {
    urnsComputed() {
      return this.submittedUrns
    },
    dateRangeValue() {
      return this.submittedDate
    },
  },
  beforeMount() {
    this.$http.get('inventory/item/respond-with/name-and-id')
      .then(response => {
        this.onlyInventoryOptions = response.data.stores
        this.inventoryOptions = this.inventoryOptions.concat(response.data.stores ?? [])
      })
      .catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
    this.$http.get('reservation/entitlement-schemes/respond-with/name-and-id')
      .then(response => {
        this.onlySchemeOptions = response.data.schemes
        this.schemeOptions = this.schemeOptions.concat(response.data.schemes ?? [])
      })
      .catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  },

  methods: {
    toggleItem(key) {
      this.$set(this.expandedItems, key, !this.expandedItems[key])
    },
    resolveEntitlementStatusVariant(status) {
      if (status === 'redeemable') return 'success'
      if (status === 'redeemed') return 'primary'
      if (status === 'used') return 'primary'
      if (status === 'expired') return 'secondary'
      if (status === 'cancelled') return 'danger'
      return 'primary'
    },
    resolveItemName(item) {
      const found = this.inventoryOptions.find(o => o._id === item)
      if (found) {
        return found.name
      }
      return ''
    },
    resolveSchemeName(item) {
      const found = this.schemeOptions.find(o => o._id === item)
      if (found) {
        return found.name
      }
      return ''
    },
    resolveStatusName(item) {
      const found = this.statusOptions.find(o => o._id === item)
      if (found) {
        return found.name
      }
      return ''
    },
    resolveStoreName(item) {
      const found = this.storeOptions.find(o => o._id === item)
      if (found) {
        return found.name
      }
      return ''
    },
    getItemImage(item) {
      const found = this.inventoryOptions.find(o => o._id === item)
      if (found) {
        return found.image
      }
      return this.defaultImage
    },
    resolveStockQuantity(item, id, key) {
      const found = this.urnReportData[key].transactions.find(trnData => trnData.item === item && trnData.transaction.irReference === id)
      if (found) {
        return found.stockAfter
      }
      return '-'
    },
    updateOptionData() {
      if (this.inventoryItems.includes('all')) {
        this.inventoryItems = this.inventoryItems.filter(el => el === 'all')
      }
    },
    updateSchemeData() {
      if (this.schemes.includes('all')) {
        this.schemes = this.schemes.filter(el => el === 'all')
      }
    },
    updateStatusData() {
      if (this.statuses.includes('all')) {
        this.statuses = this.statuses.filter(el => el === 'all')
      }
    },
    updateStoreData() {
      if (this.stores.includes('all')) {
        this.stores = this.stores.filter(el => el === 'all')
      }
    },
    downloadExcel() {
      if (this.urnReportData.length) {
        const worksheet = utils.aoa_to_sheet([
          ['', `Urn Stock Movement (${this.dateRangeValue})`],
          // ['ID', 'IR No.', 'File No.', 'Transaction Date', 'Issue To', 'Stock in qty', 'Stock out Qty', 'Adj qty', 'Item Returns', 'Stock Qty', 'Lot No.', 'NMG (FOC)', 'MPL (Buy)', 'Remarks', 'Item File No.'],
        ])
        this.urnReportData.forEach(urnData => {
          if (urnData.transactions.length) {
            const itemObject = [
              {
                name: this.resolveItemName(urnData.id),
                col1: 'Ent. ID',
                col2: 'File Number',
                col3: 'Issuance Date.',
                col4: 'Customer',
                col5: 'Store',
                col6: 'Quantity',
                col7: 'Status',
                col8: 'Internal Remarks',
              },
            ]
            utils.sheet_add_json(worksheet, itemObject, { skipHeader: true, origin: -1 })
            const data = []
            urnData.transactions.forEach(obj => {
              data.push({
                name: '',
                transactionStringID: `E${obj.entitlementID.padStart(6, '0')}`,
                customerID: obj.customerID,
                fileID: obj.fileID,
                createdAt: obj.createdAt ? this.dateFormat(obj.createdAt) : '-',
                quantity: obj.quantity,
                status: obj.status,
                internalRemarks: obj.internalRemarks,
              })
            })
            utils.sheet_add_json(worksheet, data, { skipHeader: true, origin: -1 })
          }
        })
        worksheet['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 30 }, { wch: 15 }]
        const workbook = utils.book_new()
        utils.book_append_sheet(workbook, worksheet, 'Urn-Reports')
        writeFile(workbook, `Entitlements Obligations Report - ${this.dateRangeValue}.xlsx`, { compression: true })
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Result not found! Please change the input field value and try again.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    generateReport() {
      this.$refs.entitlementsObligationReportForm.validate().then(success => {
        if (success) {
          this.submittedDate = this.dateRange
          const formData = new FormData()

          if (this.inventoryItems.includes('all')) {
            formData.append('inventoryItems', JSON.stringify(this.onlyInventoryOptions.map(urn => urn._id)))
          } else {
            formData.append('inventoryItems', JSON.stringify(this.inventoryItems))
          }
          if (this.stores.includes('all')) {
            formData.append('stores', JSON.stringify(this.onlyStoreOptions.map(urn => urn._id)))
          } else {
            formData.append('stores', JSON.stringify(this.stores))
          }
          if (this.statuses.includes('all')) {
            formData.append('statuses', JSON.stringify(this.onlyStatusOptions.map(urn => urn._id)))
          } else {
            formData.append('statuses', JSON.stringify(this.statuses))
          }
          if (this.schemes.includes('all')) {
            formData.append('schemes', JSON.stringify(this.onlySchemeOptions.map(urn => urn._id)))
          } else {
            formData.append('schemes', JSON.stringify(this.schemes))
          }
          formData.append('date', JSON.stringify(this.dateRange))
          this.$http.post('inventory/reports/generate/entitlements-obligations', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
            .then(response => {
              if (response.data.reportData) {
                this.urnReportData = response.data.reportData
                // default: expanded
                this.expandedItems = {}
                this.urnReportData.forEach((item, index) => {
                  this.$set(this.expandedItems, index, true)
                })
                this.sortDirDesc = false
                this.showUrnReportCard = true
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: response.data.message || '',
                    icon: 'BellIcon',
                    variant: 'success',
                  },
                })
              }
            })
            .catch(error => {
              if (error.data.errors) {
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'inventory') {
                    this.inventoryError = validationError.msg
                    this.inventoryValidation = true
                  } else if (validationError.param === 'dateRange') {
                    this.dateRangeError = validationError.msg
                    this.dateRangeValidation = true
                  }
                })

                const items = document.getElementsByClassName('text-danger')
                window.scrollTo({
                  top: items[0].offsetTop,
                  behavior: 'smooth',
                })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
  },
}
</script>
<style scoped>
.img-preview-block{
      box-sizing: border-box;
      width: 38px;
      height: 38px;
      border: 1px solid #D2DCEA;
      border-radius: 5px;
  }
.custom-thead-color{
   background: #4B4B4B !important;
   border: none !important;
}

.custom-thead-item-title{
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
}
.cursor-pointer {
  color: white;
}
</style>
